import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter, X, SlidersHorizontal, Star, Percent } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import ProductCard from '@/components/ProductCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [maxPrice, setMaxPrice] = useState(1000);
  const [showDiscounted, setShowDiscounted] = useState(false);
  const [showFeatured, setShowFeatured] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [products, searchTerm, selectedCategory, priceRange, showDiscounted, showFeatured, sortBy]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      
      // Calculate max price for slider
      if (response.data.length > 0) {
        const prices = response.data.map(p => p.price);
        const max = Math.max(...prices);
        setMaxPrice(Math.ceil(max / 10) * 10); // Round up to nearest 10
        setPriceRange([0, Math.ceil(max / 10) * 10]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Price range filter (considering discounts)
    filtered = filtered.filter(product => {
      const finalPrice = product.discount_percentage > 0
        ? product.price * (1 - product.discount_percentage / 100)
        : product.price;
      return finalPrice >= priceRange[0] && finalPrice <= priceRange[1];
    });

    // Discounted filter
    if (showDiscounted) {
      filtered = filtered.filter(product => product.discount_percentage > 0);
    }

    // Featured filter
    if (showFeatured) {
      filtered = filtered.filter(product => product.featured);
    }

    // Sorting
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => {
          const priceA = a.discount_percentage > 0 ? a.price * (1 - a.discount_percentage / 100) : a.price;
          const priceB = b.discount_percentage > 0 ? b.price * (1 - b.discount_percentage / 100) : b.price;
          return priceA - priceB;
        });
        break;
      case 'price-desc':
        filtered.sort((a, b) => {
          const priceA = a.discount_percentage > 0 ? a.price * (1 - a.discount_percentage / 100) : a.price;
          const priceB = b.discount_percentage > 0 ? b.price * (1 - b.discount_percentage / 100) : b.price;
          return priceB - priceA;
        });
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'discount':
        filtered.sort((a, b) => (b.discount_percentage || 0) - (a.discount_percentage || 0));
        break;
      default:
        // Keep original order (featured first, then by creation date)
        filtered.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return 0;
        });
    }

    setFilteredProducts(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setPriceRange([0, maxPrice]);
    setShowDiscounted(false);
    setShowFeatured(false);
    setSortBy('default');
  };

  const activeFiltersCount = () => {
    let count = 0;
    if (selectedCategory !== 'all') count++;
    if (priceRange[0] > 0 || priceRange[1] < maxPrice) count++;
    if (showDiscounted) count++;
    if (showFeatured) count++;
    return count;
  };

  const FilterPanel = () => (
    <div className="space-y-6">
      {/* Category Filter */}
      <div>
        <Label className="text-white mb-3 block">Categoría</Label>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="bg-gray-900/50 border-purple-900/50 text-white" data-testid="category-select">
            <Filter className="mr-2" size={16} />
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
            <SelectItem value="all">Todas las Categorías</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id || category.name} value={category.name}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price Range Filter */}
      <div>
        <Label className="text-white mb-3 block">
          Rango de Precio: ${priceRange[0]} - ${priceRange[1]}
        </Label>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          max={maxPrice}
          min={0}
          step={10}
          className="mb-4"
          data-testid="price-range-slider"
        />
        <div className="flex justify-between text-gray-400 text-sm">
          <span>${0}</span>
          <span>${maxPrice}</span>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="space-y-3">
        <Label className="text-white block">Filtros Rápidos</Label>
        
        <div className="flex items-center space-x-2">
          <Checkbox
            id="discounted"
            checked={showDiscounted}
            onCheckedChange={setShowDiscounted}
            data-testid="discounted-checkbox"
          />
          <Label htmlFor="discounted" className="flex items-center cursor-pointer text-gray-300">
            <Percent size={16} className="mr-2 text-red-400" />
            Con Descuento
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="featured"
            checked={showFeatured}
            onCheckedChange={setShowFeatured}
            data-testid="featured-checkbox"
          />
          <Label htmlFor="featured" className="flex items-center cursor-pointer text-gray-300">
            <Star size={16} className="mr-2 text-purple-400" />
            Destacados
          </Label>
        </div>
      </div>

      {/* Clear Filters */}
      {activeFiltersCount() > 0 && (
        <Button
          onClick={clearFilters}
          variant="outline"
          className="w-full border-red-600 text-red-400 hover:bg-red-600/10"
          data-testid="clear-filters-button"
        >
          <X className="mr-2" size={16} />
          Limpiar Filtros ({activeFiltersCount()})
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen py-12" data-testid="shop-page">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl sm:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-testid="shop-title">
          Nuestra Tienda
        </h1>

        {/* Search and Sort Bar */}
        <div className="glass rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-900/50 border-purple-900/50 text-white"
                data-testid="search-input"
              />
            </div>
            
            {/* Sort */}
            <div className="w-full md:w-64">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="bg-gray-900/50 border-purple-900/50 text-white" data-testid="sort-select">
                  <SlidersHorizontal className="mr-2" size={16} />
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-purple-900/50 text-white">
                  <SelectItem value="default">Predeterminado</SelectItem>
                  <SelectItem value="price-asc">Precio: Menor a Mayor</SelectItem>
                  <SelectItem value="price-desc">Precio: Mayor a Menor</SelectItem>
                  <SelectItem value="name-asc">Nombre: A-Z</SelectItem>
                  <SelectItem value="discount">Mayor Descuento</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Mobile Filter Button */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button className="md:hidden bg-purple-600 hover:bg-purple-700 relative" data-testid="mobile-filter-button">
                  <Filter size={20} />
                  {activeFiltersCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {activeFiltersCount()}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-gray-900 border-purple-900/50 text-white">
                <SheetHeader>
                  <SheetTitle className="text-white">Filtros</SheetTitle>
                  <SheetDescription className="text-gray-400">
                    Encuentra exactamente lo que buscas
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-6">
                  <FilterPanel />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Desktop Filters Sidebar */}
          <div className="hidden md:block w-64 flex-shrink-0">
            <div className="glass rounded-2xl p-6 sticky top-24">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <Filter className="mr-2" />
                Filtros
                {activeFiltersCount() > 0 && (
                  <span className="ml-2 bg-purple-600 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
                    {activeFiltersCount()}
                  </span>
                )}
              </h3>
              <FilterPanel />
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Results Info */}
            <div className="mb-6 flex justify-between items-center">
              <p className="text-gray-400">
                Mostrando <span className="text-white font-bold">{filteredProducts.length}</span> de{' '}
                <span className="text-white font-bold">{products.length}</span> productos
              </p>
            </div>

            {/* Products */}
            {loading ? (
              <div className="text-center py-20">
                <div className="text-purple-400 text-xl" data-testid="loading-spinner">Cargando productos...</div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-20 glass rounded-2xl">
                <Filter size={64} className="text-gray-600 mx-auto mb-4" />
                <div className="text-gray-400 text-xl mb-4" data-testid="no-products">No se encontraron productos</div>
                <p className="text-gray-500 mb-6">Intenta ajustar tus filtros</p>
                {activeFiltersCount() > 0 && (
                  <Button onClick={clearFilters} className="bg-purple-600 hover:bg-purple-700">
                    Limpiar Filtros
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;